import { Link } from "@inertiajs/react"

import { Badge, StatusBadge } from "@/components/ui/badge"
import { Notice } from "@/components/ui/notice"
import { Panel } from "@/components/ui/panel"
import { formatAt, formatRelative, humanDuration } from "@/lib/format"
import { adminEmailMessagePath } from "@/routes"

import {
  type EmailStatus,
  STATUS_COLOR,
  STATUS_LABEL,
} from "../../messages/types"
import { STUCK_ATTENTION_SECONDS, STUCK_FAIL_SECONDS } from "../status"
import type { HealthReport, Links } from "../types"

import { ExternalChip, Metric } from "./checks"

/**
 * Is mail leaving at all? Three panels: the ledger pipeline (where the
 * oldest-stuck age is the single most diagnostic number on the page), the
 * provider's webhook stream, and the delivery queue.
 */
export function MovementStats({
  report,
  links,
}: {
  report: HealthReport
  links: Links
}) {
  const { ledger, webhooks, queue } = report
  const stuck = ledger.awaiting_webhook
  const age = stuck.oldest_age_seconds

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <Panel
        id="movement"
        title="Pipeline"
        description={`${ledger.total} row(s) in the ledger`}
      >
        <div className="flex items-end gap-6">
          <Metric
            label="oldest has waited"
            value={humanDuration(age)}
            tone={
              // Where webhooks cannot arrive, age is not a signal — never
              // escalate it.
              !webhooks.expected
                ? "neutral"
                : age !== null && age >= STUCK_FAIL_SECONDS
                  ? "error"
                  : age !== null && age >= STUCK_ATTENTION_SECONDS
                    ? "warning"
                    : "neutral"
            }
          />
          <Metric label="awaiting a webhook" value={stuck.count} />
        </div>
        <p className="text-tertiary text-sm">
          {webhooks.expected
            ? "Live rows stay Sent until a Postmark webhook confirms them, so a " +
              "short wait is normal. A growing oldest wait means webhooks are " +
              "not arriving. Sandbox rows never receive webhooks and are excluded."
            : "A local server has no public URL, so Postmark webhooks cannot " +
              "reach it: live rows keep Sent and the oldest wait means " +
              "nothing. Sandbox rows never receive webhooks and are excluded."}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {(Object.keys(ledger.counts) as EmailStatus[]).map((status) => (
            <StatusBadge
              key={status}
              tier={
                ledger.counts[status] > 0 ? STATUS_COLOR[status] : "neutral"
              }
            >
              {STATUS_LABEL[status]} {ledger.counts[status]}
            </StatusBadge>
          ))}
        </div>
        {stuck.oldest_sent_at && (
          // The absolute stamp, not just the relative one: correlating with
          // a Render log stream needs a timestamp you can search for.
          <p className="text-tertiary text-sm">
            Oldest sent {formatAt(stuck.oldest_sent_at)}
          </p>
        )}
        {stuck.oldest_id && (
          <Link
            href={adminEmailMessagePath(stuck.oldest_id)}
            className="text-brand-secondary text-sm underline decoration-transparent transition-colors hover:decoration-current"
          >
            Inspect the oldest waiting row
          </Link>
        )}
      </Panel>

      <Panel
        id="webhooks"
        title="Provider events"
        description={`${webhooks.total} stored, kept ${webhooks.retention_days} days`}
      >
        <div className="flex items-end gap-6">
          <Metric label="last 24h" value={webhooks.count_24h} />
          <Metric
            label="unmatched, 24h"
            value={webhooks.unmatched_24h}
            tone={webhooks.unmatched_24h > 0 ? "warning" : "neutral"}
          />
        </div>
        <p className="text-tertiary text-sm">
          Last received {formatRelative(webhooks.last_received_at)}
          {webhooks.last_record_type ? ` · ${webhooks.last_record_type}` : ""}
        </p>
        {Object.keys(webhooks.by_record_type).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(webhooks.by_record_type).map(([type, count]) => (
              <Badge key={type} type="modern" color="gray" size="sm">
                {type} {count}
              </Badge>
            ))}
          </div>
        )}
        <p className="text-tertiary text-sm">
          An unmatched event authenticated but matched no ledger row. Message
          statuses never show these; this count is the only place they surface.
        </p>
        <div className="mt-auto flex flex-wrap gap-2 pt-1">
          <ExternalChip href={links.postmark_activity}>
            Postmark activity
          </ExternalChip>
        </div>
      </Panel>

      <Panel
        id="queue"
        title="Queue"
        description="ActionMailer::MailDeliveryJob"
      >
        {queue.available ? (
          <>
            <div className="flex flex-wrap items-end gap-6">
              <Metric label="queued" value={queue.queued} />
              <Metric label="running" value={queue.running} />
              <Metric
                label="discarded, 24h"
                value={queue.discarded_24h}
                tone={queue.discarded_24h > 0 ? "error" : "neutral"}
              />
              <Metric label="done, 24h" value={queue.finished_24h} />
            </div>
            <p className="text-tertiary text-sm">
              A delivery job can finish green while the Mailtrap handoff failed:
              GuardedDelivery logs that failure at ERROR and reports success to
              the job. Check the log when Mailtrap is missing mail.
            </p>
          </>
        ) : (
          <Notice tone="warning">
            The page could not read GoodJob&rsquo;s tables, so these counts are
            unknown. See the app log.
          </Notice>
        )}
        {links.good_job && (
          <div className="mt-auto flex flex-wrap gap-2 pt-1">
            <ExternalChip href={links.good_job}>GoodJob</ExternalChip>
          </div>
        )}
      </Panel>
    </div>
  )
}

export function Suppressions({
  suppressions,
}: {
  suppressions: HealthReport["suppressions"]
}) {
  return (
    <Panel
      title="Suppressions"
      description={`The suppression list blocks ${suppressions.total} address(es)`}
    >
      {suppressions.total > 0 &&
        Object.keys(suppressions.by_reason).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(suppressions.by_reason).map(([reason, count]) => (
              <Badge key={reason} color="gray" size="sm">
                {reason.replace(/_/g, " ")} {count}
              </Badge>
            ))}
          </div>
        )}
      {suppressions.recent.length === 0 ? (
        <p className="text-tertiary text-sm">The suppression list is empty.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {suppressions.recent.map((suppression) => (
            <li
              key={suppression.address}
              className="flex flex-wrap items-center gap-2"
            >
              <span className="text-secondary text-sm">
                {suppression.address}
              </span>
              <StatusBadge tier="caution">
                {suppression.reason.replace(/_/g, " ")}
              </StatusBadge>
              <span className="text-tertiary text-sm">
                {formatRelative(suppression.suppressed_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}
